import { create } from "zustand";
import {
  checkPermission,
  ROLES,
  type Permission,
  type PermissionOverride,
  type PermissionResult,
  type Role,
} from "@repo/permissions";
import { getMyPermissionOverrides } from "@/lib/api/permissions";

interface PermissionStore {
  role: Role | null;
  overrides: PermissionOverride[];
  isLoaded: boolean;
  setRole: (role: string | null) => void;
  setOverrides: (overrides: PermissionOverride[]) => void;
  /** Re-fetch overrides from the API and update the store. */
  refetchOverrides: () => Promise<void>;
  check: (permission: Permission) => PermissionResult | null;
  reset: () => void;
}

export const usePermissionStore = create<PermissionStore>((set, get) => ({
  role: null,
  overrides: [],
  isLoaded: false,

  setRole: (role) => {
    const validRole =
      role && ROLES.includes(role as Role) ? (role as Role) : null;
    set({ role: validRole });
  },

  setOverrides: (overrides) => {
    set({ overrides, isLoaded: true });
  },

  refetchOverrides: async () => {
    try {
      const data = await getMyPermissionOverrides();
      set({ overrides: data?.overrides ?? [], isLoaded: true });
    } catch {
      // Keep existing overrides on failure
    }
  },

  check: (permission) => {
    const { role, overrides } = get();
    if (!role) return null;
    return checkPermission(role, permission, {}, overrides);
  },

  reset: () => set({ role: null, overrides: [], isLoaded: false }),
}));
